import { ReactNode, useEffect, useState } from 'react';
import { Badge } from '@/components/Badge';
import { ExpandableListItem } from '@/components/List';
import { SourceFolder } from '@/types/source-folder';
import { SourcesSelection } from '@/types/source';
import { cn } from '@/lib/utils';
import type { FolderSortOption } from '../../hooks/useFolderSort';
import { FolderRow } from './FolderRow';
import { FolderSortButton } from './FolderSortButton';

interface FolderGroupProps {
    title: string;
    count: number;
    selected: boolean;
    defaultExpanded: boolean;
    loading: boolean;
    folders: SourceFolder[];
    selectedFolderId: string | null;
    onSelect: (selection: SourcesSelection) => void;
    onSelectGroup: () => void;
    onEditFolder?: (folder: SourceFolder) => void;
    onDeleteFolder?: (folder: SourceFolder) => void;
    sortOption?: FolderSortOption;
    onSortChange?: (option: FolderSortOption) => void;
    addButton?: ReactNode;
    loadMore?: ReactNode;
}

export function FolderGroup({
    title,
    count,
    selected,
    defaultExpanded,
    loading,
    folders,
    selectedFolderId,
    onSelect,
    onSelectGroup,
    onEditFolder,
    onDeleteFolder,
    sortOption,
    onSortChange,
    addButton,
    loadMore,
}: FolderGroupProps) {
    const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
    const showFolderControls = loading || folders.length > 0;

    useEffect(() => {
        if (!showFolderControls) setIsSortMenuOpen(false);
    }, [showFolderControls]);

    const sortButton =
        showFolderControls && sortOption && onSortChange ? (
            <FolderSortButton
                sortOption={sortOption}
                onSortChange={onSortChange}
                open={isSortMenuOpen}
                onOpenChange={setIsSortMenuOpen}
            />
        ) : null;
    const hasHeaderActions = !!sortButton || !!addButton;

    return (
        <ExpandableListItem
            variant="short"
            title={<span className="font-semibold">{title}</span>}
            defaultExpanded={defaultExpanded}
            selected={selected}
            onClick={onSelectGroup}
            childrenMaxHeightClass="max-h-[320px]"
            endContent={
                <>
                    <Badge
                        variant={selected ? 'number' : 'numberDark'}
                        className={cn(
                            isSortMenuOpen
                                ? 'hidden'
                                : hasHeaderActions
                                  ? 'group-hover:hidden group-focus-within:hidden'
                                  : undefined,
                        )}
                    >
                        {count}
                    </Badge>
                    {hasHeaderActions && (
                        <span
                            className={cn(
                                'flex items-center gap-2',
                                isSortMenuOpen && '[&>button]:flex',
                            )}
                        >
                            {sortButton}
                            {addButton}
                        </span>
                    )}
                </>
            }
        >
            {showFolderControls ? (
                <>
                    {folders.map((folder) => (
                        <FolderRow
                            key={folder.id}
                            folder={folder}
                            selected={selectedFolderId === folder.id}
                            onSelect={() =>
                                onSelect({ kind: 'folder', id: folder.id })
                            }
                            onEditFolder={onEditFolder}
                            onDeleteFolder={onDeleteFolder}
                        />
                    ))}
                    {loadMore}
                </>
            ) : null}
        </ExpandableListItem>
    );
}
